import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, 
  LayoutDashboard, 
  Building2,
  Handshake,
  DollarSign,
  Users,
  Plug,
  FileText,
  LogOut,
  ChevronDown,
  Settings,
  Palette,
  UserCog,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/companies', icon: Building2, label: 'Companies' },
  { path: '/admin/partners', icon: Handshake, label: 'Partners' },
  { path: '/admin/pricing', icon: DollarSign, label: 'Pricing' },
  { path: '/admin/permissions', icon: Users, label: 'Permissions' },
  { path: '/admin/integrations', icon: Plug, label: 'Integrations' },
  { path: '/admin/content', icon: FileText, label: 'Content' },
  { path: '/admin/colors', icon: Palette, label: 'Colors' },
  { path: '/admin/users', icon: UserCog, label: 'Admin Users', superAdminOnly: true },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
  { path: '/admin/maintenance', icon: Wrench, label: 'Maintenance' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);

  // Set page title
  useEffect(() => {
    document.title = "Connect | Admin Portal";
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const adminData = localStorage.getItem('admin');
    
    if (!token || !adminData) {
      navigate('/admin/login');
      return;
    }
    
    setAdmin(JSON.parse(adminData));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  if (!admin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-700/50 flex flex-col h-screen fixed left-0 top-0 bg-slate-800/50">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="font-bold text-lg text-white">CONNECT</h1>
              <p className="text-xs text-slate-400">Admin Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {NAV_ITEMS.filter(item => !item.superAdminOnly || admin?.role === 'super_admin').map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                      isActive
                        ? "bg-red-500/20 text-red-400 border border-red-500/30"
                        : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700/50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-between h-auto py-3 px-3 hover:bg-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-white truncate max-w-[120px]">
                      {admin.name || admin.username}
                    </p>
                    <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">
                      {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                    </Badge>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
              <DropdownMenuLabel className="text-slate-300">
                <div className="flex flex-col">
                  <span className="font-medium">{admin.name || admin.username}</span>
                  <span className="text-xs text-slate-400">{admin.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 cursor-pointer focus:bg-slate-700 focus:text-red-400">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 overflow-auto bg-slate-900">
        <Outlet context={{ admin }} />
      </main>
    </div>
  );
}
